import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles, createMuiTheme, ThemeProvider } from "@material-ui/core/styles";

import { T, FONT_SANS, PHOTO_STAGES, formatClock } from "../constants";
import Toast from "../components/Toast";
import { cx, useHydrated, useUi } from "../ui";
import PlateSearch from "./components/PlateSearch";
import IntakeStepper from "./components/IntakeStepper";
import FlowForecast from "./components/FlowForecast";
import BookingQuickView from "../components/BookingQuickView";
import PickupRequestModal from "../components/PickupRequestModal";
import ExpectedColumn from "./components/ExpectedColumn";
import ArrivalsQueueColumn from "./components/ArrivalsQueueColumn";
import OnSiteColumn from "./components/OnSiteColumn";
import OperationsPreviewBlocks from "./components/OperationsPreviewBlocks";
import QuickBookingModal from "./components/QuickBookingModal";
import MatchBookingModal from "./components/MatchBookingModal";
import PropertySettingsModal from "./components/PropertySettingsModal";
import useArrivalsQueue from "./useArrivalsQueue";
import useOnSiteSessions from "./useOnSiteSessions";
import useValetSessionActions from "./useValetSessionActions";
import useVehiclePhotos from "./useVehiclePhotos";
import useVehiclePhotoMode from "./useVehiclePhotoMode";
import { friendlyValetError } from "./valetErrors";
import { JOB_ACTION, deskActionsFor } from "../jobActions";
import { isUnmatched, mapSessionToArrival } from "../helpers";
import { ARRIVAL } from "../arrival";
import { withInstallation } from "../../../components/InstallationContext";
import {
  isConciergePageEnabled,
  isAnprCameraEnabled,
  VEHICLE_PHOTO_MODES,
} from "../../../lib/conciergeFeature";
import authManager, { PERMISSIONS } from "../../../lib/authManager";

const conciergeTheme = createMuiTheme({ typography: { fontFamily: FONT_SANS } });

const useStyles = makeStyles({
  root: {
    background: T.bg,
    color: T.fg,
    fontFamily: FONT_SANS,
    // Neutralise the console's 20px main gutter so this page owns its own padding.
    margin: -20,
    minHeight: "calc(100% + 40px)",
    padding: 24,
    boxSizing: "border-box",
  },
  inner: { maxWidth: 1600, margin: "0 auto" },
  header: {
    borderRadius: 14,
    background: T.primary,
    color: "#fff",
    padding: "20px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 32,
    flexWrap: "wrap",
    marginBottom: 24,
  },
  statRail: { display: "flex", alignItems: "center", gap: 28 },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  actions: { display: "flex", gap: 8, alignItems: "center" },
  quiet: { padding: "8px 14px", fontSize: 13, borderRadius: 8 },
  runnerLink: { textDecoration: "none", color: T.primary },
  title: { fontWeight: 700, color: T.fg },
  headerTitle: { margin: 0, fontSize: 26, fontWeight: 300, lineHeight: 1 },
  railDivider: { height: 24, width: 1, background: "rgba(255,255,255,0.18)" },
  searchWrap: { marginBottom: 24 },
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 24,
    alignItems: "stretch",
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
  },
  boardWithoutArrivals: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
  },
  forecast: { marginTop: 24, minWidth: 0 },
});

function ConciergeDashboard({ facility, installation }) {
  const cls = useStyles();
  const ui = useUi();
  const anprEnabled = isAnprCameraEnabled(facility);
  const vehiclePhotoMode = useVehiclePhotoMode({
    facility,
    brandingKey: installation?.brandingKey,
  });

  /* The same path App.js mounts the runner queue on, keyed the way every other
   * link in the console is. */
  const runnerQueuePath = installation?.brandingKey
    ? `/${installation.brandingKey}/runner`
    : null;

  // Real valet sessions off the API.
  const {
    items: arrivals,
    error: arrivalsError,
    refetch: refetchArrivals,
  } = useArrivalsQueue(facility?.id);

  const {
    items: onSite,
    tabs: onSiteTabs,
    unassigned,
  } = useOnSiteSessions(facility?.id);

  const {
    ensureSession,
    claimSession,
    dismissSession,
    dismissing,
    saveGuestInfo,
    requestReturn,
    scheduleReturn,
    cancelReturn,
    confirmReturned,
  } = useValetSessionActions(facility?.id);

  // Guards the intake finish, which now writes to the server.
  const [claiming, setClaiming] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);

  const [active, setActive] = useState(null);
  const [vehicleInput, setVehicleInput] = useState("");
  const [toast, setToast] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  /* null while closed. `session` is the car already on the forecourt whose
   * booking is being found; a New arrival opens the same list with none. */
  const [matching, setMatching] = useState(null);
  const [linking, setLinking] = useState(false);
  const [quickInitial, setQuickInitial] = useState(null);
  const [viewCar, setViewCar] = useState(null);
  /* The row the desk is amending, held apart from viewCar so the booking view
   * stays open underneath and is returned to on save or cancel. */
  const [editCar, setEditCar] = useState(null);
  /* The car whose pickup the desk is confirming, if any. */
  const [pickupFor, setPickupFor] = useState(null);
  const [runningAction, setRunningAction] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Vehicle condition photos: captured on the tablet, uploaded to S3, and
  // recorded against the booking. The hook owns the per-angle upload state.
  const {
    photos,
    capture: capturePhoto,
    retry: retryPhoto,
    reset: resetPhotos,
    allDone: allPhotos,
    anyBusy: uploadingPhotos,
    anyError: photoError,
    hydrating: hydratingPhotos,
  } = useVehiclePhotos({
    bookingId:
      vehiclePhotoMode === VEHICLE_PHOTO_MODES.DISABLED
        ? null
        : active?.bookingId,
    facilityId: facility?.id,
    stage: PHOTO_STAGES.INTAKE,
    valetSessionId: active?.valetSessionId ?? null,
  });

  const hydrated = useHydrated();

  useEffect(() => {
    if (!arrivalsError) return;
    setToast("Could not load the arrivals queue. Retrying...");
  }, [arrivalsError]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3400);
    return () => clearTimeout(t);
  }, [toast]);

  const resetIntakeState = (booking) => {
    // Aborts any upload still in flight and revokes the tile previews.
    resetPhotos();
    setVehicleInput(booking?.vehicle ?? "");
  };

  /* What used to be checked on the Accept keys tap, now checked before intake
   * opens: neither of these can be answered from inside the stepper, so
   * starting one would only strand the operator three steps in. */
  const intakeBlocker = (booking) => {
    if (isUnmatched(booking))
      return "Cannot start intake - this plate has no booking and is not a sanctioned plate. Confirm the booking with the front desk first.";

    if (!booking.bookingId)
      return "No booking on file for this car - add details to create a walk-up booking first.";

    return null;
  };

  const claimArrival = (id) => {
    if (active) return;
    const booking = arrivals.find((b) => b.id === id);
    if (!booking) return;

    const blocked = intakeBlocker(booking);
    if (blocked) {
      setToast(blocked);
      return;
    }

    setActive(booking);
    resetIntakeState(booking);
  };

  const dismissArrival = async (id) => {
    const booking = arrivals.find((b) => b.id === id);
    if (!booking) return;

    try {
      await dismissSession(booking.valetSessionId ?? booking.id);
      setToast(`${booking.plate} marked as drop-off (Uber/taxi) - removed from valet queue.`);
    } catch (e) {
      setToast(friendlyValetError(e, "remove this car from the queue"));
      refetchArrivals();
    }
  };

  /*
   * Real booking off the intake list, so intake can claim it for real
   */
  const beginIntake = async (booking) => {
    if (!booking) return false;
    if (active) {
      setToast("Finish the current intake before starting another.");
      return false;
    }
    const blocked = intakeBlocker(booking);
    if (blocked) {
      setToast(blocked);
      return false;
    }

    setActive(booking);
    resetIntakeState(booking);
    return true;
  };

  /*
   * One list, two jobs. A car already in the queue gets the booking attached to
   * the session it arrived on; a New arrival has no session yet, so one is
   * opened against the booking when the intake finishes. Both go on to the same
   * intake, which is why the booking picked here is what it runs on.
   */
  const pickBooking = async (booking) => {
    const session = matching?.session ?? null;

    if (!session) {
      if (!beginIntake(booking)) return;
      setMatching(null);
      setToast(`Manual pickup - ${booking.guestName} (${booking.plate}) selected without LPR.`);
      return;
    }

    setLinking(true);

    try {
      const linked = await ensureSession({
        bookingId: booking.bookingId,
        valetSessionId: session.valetSessionId ?? session.id,
      });
      const valetSessionId = linked?.id ?? session.valetSessionId ?? session.id;

      /* The camera read is the car's identity, so it survives the booking's own
       * guest-entered plate - the same rule mapSessionToArrival follows. */
      const row = {
        ...booking,
        id: valetSessionId,
        valetSessionId,
        plate: session.plate ?? booking.plate,
        hasBooking: true,
      };

      setMatching(null);
      if (!beginIntake(row)) return;

      setToast(`${row.plate} matched to ${booking.guestName}.`);
      refetchArrivals();
    } catch (e) {
      setToast(friendlyValetError(e, "match this car to a booking"));
    } finally {
      setLinking(false);
    }
  };

  /* A walk-up made from a car already on the forecourt is created against that
   * session, so the booking lands on the row the guest is standing next to. */
  const startWalkUp = () => {
    const session = matching?.session ?? null;

    setMatching(null);
    setQuickInitial(
      session
        ? {
            plate: session.plate,
            valetSessionId: session.valetSessionId ?? session.id,
          }
        : null,
    );
    setQuickOpen(true);
  };

  const releaseActive = () => {
    // Once the claim has landed the session is CLAIMED server-side, so the
    // overlay backdrop must not put the car back in the queue.
    if (!active || claiming) return;
    setActive(null);
    resetIntakeState();
  };

  /* Answers whether the number landed, so the stepper only moves on once the
   * booking actually carries it. */
  const submitPhone = async (phone) => {
    if (!active || savingPhone) return false;
    setSavingPhone(true);

    try {
      await saveGuestInfo({ bookingId: active.bookingId, guestPhone: phone });
      setActive((a) => (a ? { ...a, phone } : a));
      setToast("Phone number saved to booking.");
      return true;
    } catch (e) {
      setToast(friendlyValetError(e, "save the phone number"));
      return false;
    } finally {
      setSavingPhone(false);
    }
  };

  /*
   * The one server write of the intake flow: the valet session becomes CLAIMED
   * and the booking starts. Both happen here, after the photos, rather than on
   * the Accept keys tap.
   */
  const finishIntake = async () => {
    if (!active || claiming) return;
    setClaiming(true);

    try {
      const vehicle = vehicleInput.trim();

      // Vehicle make lands on the booking's carType before the claim, so a
      // failed claim can be retried without losing what was typed.
      if (active.bookingId && vehicle !== (active.vehicle ?? "")) {
        await saveGuestInfo({ bookingId: active.bookingId, carType: vehicle });
        setActive((a) => (a ? { ...a, vehicle: vehicle || null } : a));
      }

      let sessionId = active.valetSessionId;

      if (!sessionId) {
        const session = await ensureSession({ bookingId: active.bookingId });
        sessionId = session?.id;
      }

      await claimSession(sessionId);

      setToast(`Keys accepted. ${active.plate} queued for a runner to park.`);
      setActive(null);
    } catch (e) {
      // Stay on the keys step with the car still active so it can be retried.
      setToast(friendlyValetError(e));
    } finally {
      setClaiming(false);
      refetchArrivals();
    }
  };

  /*
   * The desk calls a car up on the guest's behalf - the same thing the guest can
   * do from their pickup link. The mutation returns the session, so the row the
   * concierge is looking at flips to Requested without waiting for a poll.
   */
  /* Either way the car goes into the runner queue now: a time says when it is
   * wanted, no time means now. On a pickup already queued, the same call moves
   * it. The desk picks which in the modal, and the toast says which it got. */
  const lodgePickup = async (car, requestedAt = null) => {
    if (!car?.valetSessionId) return null;

    const moving =
      car.status === "REQUESTED" || car.status === "RUNNER_ASSIGNED";

    if (requestedAt) {
      const session = await scheduleReturn(car.valetSessionId, requestedAt);
      setToast(
        `Pickup ${moving ? "moved" : "booked"} for ${car.plate} at ${formatClock(
          requestedAt,
        )} - runner queue updated.`,
      );

      return session;
    }

    const session = await requestReturn(car.valetSessionId);
    setToast(
      moving
        ? `${car.plate} wanted now - runner queue updated.`
        : `Pickup lodged for ${car.plate} - runner queue updated.`,
    );

    return session;
  };

  /* Handing a car over is the runner queue's write, so the desk is only
   * offered it where the operator actually holds that permission. */
  const deskActions = deskActionsFor({
    row: viewCar,
    canHandOver: authManager.hasPermission(
      PERMISSIONS.RUNNER_QUEUE_MANAGE,
      installation?.id,
    ),
  });

  /*
   * Every row on the board opens the same booking view, so the one action it
   * offers is whatever that car's stage allows - and running it is this one
   * dispatcher. The server refuses a car that owes money, so a refusal comes
   * back as a toast rather than being pre-empted here.
   */
  const runDeskAction = async (car, action) => {
    if (!action) return;

    /* Both of these hand straight over to another modal, so this one goes. */
    if (action.key === JOB_ACTION.START_INTAKE) {
      setViewCar(null);
      beginIntake(car);
      return;
    }

    if (action.key === JOB_ACTION.MATCH) {
      setViewCar(null);
      setMatching({ session: car });
      return;
    }

    /* The balance and the shift window are the modal's to settle now, so this
     * one opens rather than calling the car up on the spot. Moving a queued
     * pickup is the same choice of now or a time. */
    if (
      action.key === JOB_ACTION.LODGE_PICKUP ||
      action.key === JOB_ACTION.CHANGE_PICKUP
    ) {
      setPickupFor(car);
      return;
    }

    setRunningAction(true);

    try {
      if (action.key === JOB_ACTION.HAND_OVER) {
        await confirmReturned(car.valetSessionId);
        setToast(`${car.plate} handed to ${car.guestName || "the guest"}.`);
        /* The car has left; there is nothing left to look at. */
        setViewCar(null);
        return;
      }

      const session =
        action.key === JOB_ACTION.CANCEL_PICKUP
          ? await cancelReturn(car.valetSessionId)
          : await lodgePickup(car);

      if (action.key === JOB_ACTION.CANCEL_PICKUP) {
        setToast(
          `Pickup cancelled for ${car.plate}.`,
        );
      }

      /* The car is still on the board, so the view stays open on what the
       * server now says - which is also what decides the next action. */
      if (session) {
        setViewCar((current) =>
          current && current.id === car.id
            ? mapSessionToArrival(session)
            : current,
        );
      }
    } catch (e) {
      setToast(friendlyValetError(e, "update this car"));
    } finally {
      setRunningAction(false);
    }
  };

  /*
   * What happens once a booking exists: it is said out loud and it joins the
   * lists, and that is all. Intake, the off-site ticket and the photos are
   * started from the queue the booking has just landed in - the desk is often
   * not ready to do any of them the instant the guest card is filled in, and
   * a modal that hands straight over to another one cannot be stopped.
   */
  const continueAfterCreate = (b, reasons) => {
    const notifyMsg = reasons.length > 0 ? ` · notified ${reasons.join(", ")}` : "";

    if (b.arrivalMode !== ARRIVAL.NOW) {
      setToast(`Booking created for ${b.guestName}${notifyMsg}`);
      refetchArrivals();
      return;
    }

    setToast(
      b.paymentRequired === false
        ? `Walk-up booking created for ${b.guestName} - no payment due on this guest card${notifyMsg}.`
        : `Walk-up booking created for ${b.guestName}${notifyMsg}.`,
    );
    refetchArrivals();
  };

  return (
    <ThemeProvider theme={conciergeTheme}>
      <Box className={cls.root}>
        <div className={cls.inner}>
          <div className={cls.titleBar}>
            <Typography variant="h5" component="span" className={cls.title}>Concierge · Live</Typography>
            <div className={cls.actions}>
              <button
                type="button"
                className={cx(ui.ghostBtn, cls.quiet)}
                onClick={() => setSettingsOpen(true)}
              >
                Capacity &amp; policy
              </button>
              {/* A new tab, not a route: the desk is mid-arrival more often
                  than not, and coming back to a reloaded board loses it. */}
              {runnerQueuePath && (
                <a
                  href={runnerQueuePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cx(ui.ghostBtn, cls.quiet, cls.runnerLink)}
                >
                  Runner queue &#8599;
                </a>
              )}
            </div>
          </div>

          <div className={cls.searchWrap}>
            <PlateSearch facilityId={facility?.id} onOpenCar={setViewCar} />
          </div>

          <div className={cx(cls.board, !anprEnabled && cls.boardWithoutArrivals)}>
            <ExpectedColumn
              hydrated={hydrated}
              onSite={onSite}
              onOpenCar={setViewCar}
              onNewBooking={() => setQuickOpen(true)}
              onNewArrival={
                anprEnabled ? undefined : () => setMatching({ session: null })
              }
            />
            {anprEnabled && (
              <ArrivalsQueueColumn
                arrivals={arrivals}
                onOpenCar={setViewCar}
                onClaim={claimArrival}
                onDismiss={dismissArrival}
                onMatch={(arrival) => setMatching({ session: arrival })}
                onNewArrival={() => setMatching({ session: null })}
                dismissing={dismissing}
                disabled={Boolean(active)}
              />
            )}
            <OnSiteColumn
              tabs={onSiteTabs}
              unassigned={unassigned}
              hydrated={hydrated}
              onOpenCar={setViewCar}
            />
          </div>

          <OperationsPreviewBlocks
            installation={installation}
            sessions={onSite}
            onOpenCar={setViewCar}
          />

          <div className={cls.forecast}>
            <FlowForecast installation={installation} />
          </div>
        </div>

        {active && (
          <IntakeStepper
            booking={active}
            photos={photos}
            onCapture={capturePhoto}
            onRetryPhoto={retryPhoto}
            allPhotos={allPhotos}
            uploadingPhotos={uploadingPhotos}
            hydratingPhotos={hydratingPhotos}
            vehicleInput={vehicleInput}
            onVehicleInputChange={setVehicleInput}
            onSubmitPhone={submitPhone}
            savingPhone={savingPhone}
            onFinish={finishIntake}
            claiming={claiming}
            onClose={releaseActive}
            vehiclePhotoMode={vehiclePhotoMode}
          />
        )}

        {settingsOpen && (
          <PropertySettingsModal
            installationId={installation?.id}
            timezone={installation?.timeZoneName}
            onClose={() => setSettingsOpen(false)}
          />
        )}

        {toast && <Toast>{toast}</Toast>}

        {pickupFor && (
          <PickupRequestModal
            row={pickupFor}
            onClose={() => setPickupFor(null)}
            onLodged={async (requestedAt) => {
              const car = pickupFor;

              try {
                const session = await lodgePickup(car, requestedAt);
                setPickupFor(null);

                /* The car is still on the board, so the view stays open on what
                 * the server now says - which is also what decides the next
                 * action. */
                if (session) {
                  setViewCar((current) =>
                    current && current.id === car.id
                      ? mapSessionToArrival(session)
                      : current,
                  );
                }
              } catch (e) {
                setToast(friendlyValetError(e, "update this car"));
              }
            }}
          />
        )}

        {viewCar && (
          <BookingQuickView
            row={viewCar}
            facilityId={facility?.id}
            action={deskActions.primary}
            secondaryAction={deskActions.secondary}
            actionBusy={runningAction}
            onAction={runDeskAction}
            onEdit={setEditCar}
            onClose={() => setViewCar(null)}
          />
        )}

        {editCar && (
          <QuickBookingModal
            mode="edit"
            initial={editCar}
            onClose={() => setEditCar(null)}
            onSaved={(booking) => {
              setEditCar(null);

              /* `id` stays the row's own - a car on the board is identified by
               * its valet session, not by the booking just saved. */
              setViewCar((current) =>
                current ? { ...current, ...booking, id: current.id } : current,
              );
              setToast(`Booking updated for ${booking.guestName}`);
              refetchArrivals();
            }}
          />
        )}

        {quickOpen && (
          <QuickBookingModal
            initial={quickInitial}
            onClose={() => {
              setQuickOpen(false);
              setQuickInitial(null);
            }}
            onCreated={(b, reasons) => {
              setQuickOpen(false);
              setQuickInitial(null);
              continueAfterCreate(b, reasons);
            }}
          />
        )}

        {matching && (
          <MatchBookingModal
            session={matching.session}
            busy={linking}
            onClose={() => setMatching(null)}
            onPick={pickBooking}
            onWalkUp={startWalkUp}
            onDismiss={
              matching.session
                ? () => {
                    const { session } = matching;
                    setMatching(null);
                    dismissArrival(session.id);
                  }
                : undefined
            }
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export default withInstallation(ConciergeDashboard)
